import { useState, useCallback, useMemo, useEffect } from 'react';

import { ColumnProps } from '../types';

// only support radio
export const useFilter = <T extends Record<string, any>>(
  columns: ColumnProps<T>[],
  originData?: T[],
) => {
  // { cloumn.key, [filter.value.idx, hasOnFilterChange] }
  const [filterState, setFilterState] = useState(
    columns.reduce(
      (m, n) => {
        if (n.key && n.filters?.length && typeof n.defaultFilterIdx !== 'undefined') {
          m[n.key] = [n.defaultFilterIdx, !!n.onFilterChange];
        }

        return m;
      },
      {} as Record<string, [number, boolean]>,
    ),
  );

  useEffect(() => {
    setFilterState(
      columns.reduce(
        (m, n) => {
          if (n.key && n.filters?.length && typeof n.filterIdx !== 'undefined') {
            m[n.key] = [n.filterIdx, !!n.onFilterChange];
          }

          return m;
        },
        {} as Record<string, [number, boolean]>,
      ),
    );
  }, [setFilterState, columns]);

  const handleFilter = useCallback(
    (column: ColumnProps<T>, idx: number) => {
      if (!column.key || !column.filters?.length) {
        return;
      }

      const next = { ...filterState };

      const key = column.key as string;

      if (next[key]?.[0] === idx) {
        delete next[key];
      } else {
        next[column.key as string] = [idx, !!column.onFilterChange];
      }

      setFilterState(next);

      if (column.onFilterChange) {
        const filters = Object.entries(next).reduce(
          (m, n) => ({
            ...m,
            // cloumn.key: filter value
            [n[0]]: n[1][0],
          }),
          {},
        );

        // for remote method
        column.onFilterChange(filters);
      }
    },
    [filterState, setFilterState],
  );

  const filtedData = useMemo(() => {
    const filterArr = Object.entries(filterState)
      // filter has 'OnFilterChange' prop column
      .filter(([k, v]) => !v[1])
      .map(([k, v]) => [k, v[1]] as const);

    return filterArr?.length
      ? originData?.filter(x => {
          return filterArr.every(([k, v]) => x[k] === v);
        })
      : originData;
  }, [originData, filterState]);

  return {
    filterState,
    handleFilter,
    filtedData,
  };
};
